import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import QRPreview from "@/components/QRPreview";
import { DESTINATIONS, destMeta, buildEncodedData, DEFAULT_DESIGN } from "@/lib/qr";
import { downloadQR } from "@/lib/download";
import api, { apiError } from "@/lib/api";

const STATUS_STYLE = { draft: "bg-muted text-muted-foreground", active: "bg-emerald-500/10 text-emerald-500", paused: "bg-amber-500/10 text-amber-500", archived: "bg-neutral-500/10 text-neutral-500" };

function Stat({ icon, label, value, tint }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className={`h-9 w-9 rounded-xl grid place-items-center mb-3 ${tint}`}><Icon name={icon} className="h-4 w-4" /></div>
      <div className="text-2xl font-display font-extrabold">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [sort, setSort] = useState("created_desc");
  const [folder, setFolder] = useState("all");
  const [folders, setFolders] = useState([]);
  const [stats, setStats] = useState(null);
  const [plan, setPlan] = useState(null);
  const atLimit = plan && plan.limits.qr != null && plan.usage.qr >= plan.limits.qr;
  const watermark = !!(plan && plan.entitlements && plan.entitlements.watermark);

  const load = () => {
    setLoading(true);
    api.get("/qr/paged", { params: { page: 1, page_size: 6, sort: "created_desc" } })
      .then(({ data }) => setItems(data.items || [])).catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { api.get("/analytics/overview").then(({ data }) => setStats(data)).catch(() => {}); api.get("/folders").then(({ data }) => setFolders(data.folders || [])).catch(() => {}); api.get("/company/plan").then(({ data }) => setPlan(data)).catch(() => {}); }, []);

  const goCreate = () => { if (atLimit) return toast.error(`You've reached your plan limit of ${plan.limits.qr} QR codes. Upgrade in Billing to create more.`); nav("/app/builder"); };

  const act = async (id, action) => {
    try {
      if (action === "delete") { if (!window.confirm("Delete this QR permanently?")) return; await api.delete(`/qr/${id}`); toast.success("Deleted"); }
      else if (action === "archive") { await api.post(`/qr/${id}/archive`); toast.success("Archived"); }
      else if (action === "duplicate") { await api.post(`/qr/${id}/duplicate`); toast.success("Duplicated"); }
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const download = async (it) => {
    const encoded = buildEncodedData({ type: it.destination_type, mode: it.mode, data: it.data, publicId: it.public_id, vcardMode: it.vcard_mode, dynamicUrl: it.dynamic_url });
    try { await downloadQR({ ...DEFAULT_DESIGN, ...(it.design || {}) }, encoded, it.name, "png", watermark); toast.success("PNG downloaded"); }
    catch { toast.error("Download failed"); }
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl font-display font-extrabold">QR Codes</h1><p className="text-sm text-muted-foreground">Manage every code your company creates.</p></div>
        <div className="flex gap-2">
          {can("qr.edit") && <button data-testid="dash-bulk" onClick={() => nav("/app/bulk")} className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold hover:bg-accent flex items-center gap-1.5"><Icon name="Layers" className="h-4 w-4" />Bulk Creator</button>}
          {can("qr.edit") && <button data-testid="dash-generate" onClick={goCreate} className="rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-bold hover:opacity-90 flex items-center gap-1.5"><Icon name="Plus" className="h-4 w-4" />Generate QR code</button>}
        </div>
      </div>

      {plan && plan.limits.qr != null && (
        <div data-testid="usage-banner" className={`rounded-2xl border p-4 mb-6 flex flex-wrap items-center justify-between gap-3 ${atLimit ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-card"}`}>
          <div className="text-sm">{plan.plan && <><span className="font-semibold capitalize">{plan.plan} plan</span> · </>}<span data-testid="qr-usage-text">{plan.usage.qr} of {plan.limits.qr} QR codes used</span>{atLimit && <span className="text-amber-500 font-semibold"> — limit reached</span>}</div>
          {plan.plan && <Link to="/app/plan-usage" className="text-sm font-semibold text-primary flex items-center gap-1">Plan & usage <Icon name="ArrowRight" className="h-3.5 w-3.5" /></Link>}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat icon="QrCode" label="Total QR codes" value={stats.total_qrs} tint="bg-primary/10 text-primary" />
          <Stat icon="Radio" label="Active" value={stats.active_qrs} tint="bg-emerald-500/10 text-emerald-500" />
          <Stat icon="MousePointerClick" label="Total scans (30d)" value={stats.total_scans} tint="bg-cyan-500/10 text-cyan-500" />
          <Stat icon="Users" label="Unique (est.)" value={stats.unique_estimate} tint="bg-amber-500/10 text-amber-500" />
        </div>
      )}

      {/* Recent QR codes header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-display font-bold">Recent QR codes</h2>
        <button data-testid="view-all-qr" onClick={() => nav("/app/qr-codes")} className="text-sm font-semibold text-primary flex items-center gap-1 hover:underline">View all QR codes <Icon name="ArrowRight" className="h-3.5 w-3.5" /></button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-20 text-center">
          <div className="h-14 w-14 rounded-2xl bg-primary/10 grid place-items-center mx-auto mb-4"><Icon name="QrCode" className="h-7 w-7 text-primary" /></div>
          <h3 className="font-display font-bold">No QR codes yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first code to get started.</p>
          <button onClick={() => nav("/app/builder")} className="rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:opacity-90">Generate QR code</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="qr-grid">
          {items.map((it) => {
            const meta = destMeta(it.destination_type);
            const encoded = buildEncodedData({ type: it.destination_type, mode: it.mode, data: it.data, publicId: it.public_id, vcardMode: it.vcard_mode, dynamicUrl: it.dynamic_url });
            return (
              <div key={it.id} data-testid={`qr-card-${it.id}`} className="rounded-2xl border border-border bg-card p-4 hover:border-primary/40 transition-colors group">
                <div className="flex gap-4">
                  <div className="h-20 w-20 rounded-xl bg-white p-1.5 shrink-0"><QRPreview design={{ ...DEFAULT_DESIGN, ...(it.design || {}), frame: "none", size: 120 }} data={encoded} frameless watermark={watermark} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[it.status]}`}>{it.status}</span><span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-muted text-muted-foreground">{it.mode}</span></div>
                    <h3 className="font-semibold text-sm mt-1.5 truncate">{it.name}</h3>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Icon name={meta.icon} className="h-3 w-3" />{meta.name}</div>
                    {(it.folder || (it.tags || []).length > 0) && <div className="flex flex-wrap items-center gap-1 mt-1">{it.folder && <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-muted text-muted-foreground flex items-center gap-0.5"><Icon name="Folder" className="h-2.5 w-2.5" />{it.folder}</span>}{(it.tags || []).slice(0, 3).map((t) => <span key={t} className="text-[10px] rounded-full px-1.5 py-0.5 bg-primary/10 text-primary">#{t}</span>)}</div>}
                    <div className="text-xs text-muted-foreground mt-1.5">{it.mode === "dynamic" ? <span className="flex items-center gap-1"><Icon name="MousePointerClick" className="h-3 w-3" />{it.scan_count} scans</span> : <span className="text-muted-foreground/70">No analytics (static)</span>}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                  <IconBtn testid={`edit-${it.id}`} icon={can("qr.edit") ? "Pencil" : "Eye"} label={can("qr.edit") ? "Edit" : "View"} onClick={() => nav(`/app/builder/${it.id}`)} />
                  <IconBtn testid={`analytics-${it.id}`} icon="BarChart3" label="Stats" onClick={() => nav(`/app/analytics/${it.id}`)} />
                  <IconBtn testid={`download-${it.id}`} icon="Download" label="Download" onClick={() => download(it)} />
                  {can("qr.edit") && <IconBtn testid={`duplicate-${it.id}`} icon="Copy" label="Duplicate" onClick={() => act(it.id, "duplicate")} />}
                  {can("qr.edit") && <IconBtn testid={`archive-${it.id}`} icon="Archive" label="Archive" onClick={() => act(it.id, "archive")} />}
                  {can("qr.delete") && <IconBtn testid={`delete-${it.id}`} icon="Trash2" label="Delete" onClick={() => act(it.id, "delete")} danger />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}

const IconBtn = ({ icon, label, onClick, testid, danger }) => (
  <button data-testid={testid} onClick={onClick} title={label} className={`h-8 w-8 grid place-items-center rounded-lg hover:bg-accent transition-colors ${danger ? "hover:text-destructive" : ""}`}><Icon name={icon} className="h-4 w-4" /></button>
);
